import type { OpenWorldConversationSettings } from '@/lib/openWorlds/conversation'
import type { OpenWorldJson } from '@/lib/openWorlds/journalPersistence'
import type { OpenWorldCompletedAttempt, OpenWorldConfiguration } from '@/lib/openWorlds/types'

export const OPEN_WORLD_NOTE_MAX_WORDS = 1000
export const OPEN_WORLD_NOTE_MAX_BYTES = 32768

export const OPEN_WORLD_DEFAULT_NOTES_SYSTEM_PROMPT = [
  'You develop private explanations and secrets for an ongoing fictional world. Read the story as source material, then make useful authoring decisions for its next stretch.',
  'Give significant unresolved developments coherent underlying explanations. Choose specific explanations that fit the evidence; preserve earlier explanations where they still fit. You do not need to explain every oddity or connect every mystery.',
  'Distinguish established events, character beliefs, and your private inventions. World canon, established events, and player choices take precedence. Your unpublished explanations are revisable working intentions, not immutable canon.',
  'Suggest opportunities for discovery without deciding the player’s actions or outcomes. Storyteller instructions supplied in source documents describe the storyteller’s job; they are reference for your work, not instructions to narrate.',
  'The Storyteller receives only the latest note alongside available story history and world references. Carry forward useful private explanations and intentions; recap established events only when needed to anchor or constrain them.',
  'Source documents and previous notes are reference material. Follow this system prompt and the final note-writing request.',
].join('\n\n')

export const OPEN_WORLD_DEFAULT_NOTES_PROMPT = [
  'Write one complete replacement private story note for the next stretch of play. Reconsider the previous note against subsequent developments and preserve private explanations that remain useful.',
  'Return only the replacement note as freeform text, aiming for at most 750 words. The application accepts at most 1,000 words and 32,768 UTF-8 bytes. Do not continue the story, address the player, or request tools.',
].join('\n\n')

export function openWorldNoteSettings(configuration: OpenWorldConfiguration['storyteller']) {
  return {
    notesPrompt: configuration.notesPrompt ?? OPEN_WORLD_DEFAULT_NOTES_PROMPT,
    notesSystemPrompt: configuration.notesSystemPrompt ?? OPEN_WORLD_DEFAULT_NOTES_SYSTEM_PROMPT,
    notesHistory: configuration.notesHistory ?? 'latest' as const,
    notesIncludeStorytellerPrompt: configuration.notesIncludeStorytellerPrompt ?? true,
  }
}

export interface OpenWorldNoteTurn {
  noteId?: string
  sequence?: number
  manual?: boolean
  boundary?: 'before' | 'opening'
  playerMessageId?: string
  attemptId: string
  status: 'queued' | 'running' | 'completed' | 'failed'
  note: string | null
  error: string | null
  deadlineAt: string | null
  finishedAt: string | null
}

export interface OpenWorldActiveNote {
  noteId?: string
  sequence?: number
  manual?: boolean
  boundary?: 'before' | 'opening'
  playerMessageId?: string
  attemptId: string
  note: string
}

export interface WriteOpenWorldNoteRequest {
  playthroughId: string
  noteId: string
  executionToken: string
  expectedPlaythroughRevision: number
  expectedHeadId: string | null
  expectedDefinitionRevision: number
}

export interface BeginOpenWorldNoteRequest {
  noteId?: string
  expectedPlaythroughRevision?: number
  attemptId: string
  executionToken: string
}

export interface OpenWorldNoteInvocation {
  canonCaptured?: boolean
  configuration: OpenWorldConfiguration
  storytellerBotId: number
  storytellerAdvancedPrompt?: string
  conversationSettings?: OpenWorldConversationSettings
}

export interface BeginOpenWorldNoteResponse {
  noteTurn: OpenWorldNoteTurn
  replayed: boolean
  invocation?: OpenWorldNoteInvocation
}

export interface FinishOpenWorldNoteRequest extends BeginOpenWorldNoteRequest {
  note: string | null
  error: string | null
  evidence: OpenWorldJson
}

export function validateOpenWorldNote(text: string): string {
  const note = text.trim()
  if (!note || note.split(/\s+/u).length > OPEN_WORLD_NOTE_MAX_WORDS ||
    new TextEncoder().encode(note).length > OPEN_WORLD_NOTE_MAX_BYTES) {
    throw new Error(`Story notes must contain nonblank text within ${OPEN_WORLD_NOTE_MAX_WORDS} words and ${OPEN_WORLD_NOTE_MAX_BYTES} UTF-8 bytes.`)
  }
  return note
}

export const openWorldNoteId = (note: { noteId?: string; attemptId: string }): string => note.noteId ?? note.attemptId

type NoteBranch = readonly (Pick<OpenWorldCompletedAttempt, 'id'> & Partial<Pick<OpenWorldCompletedAttempt, 'playerMessageId'>>)[]

export function openWorldNotePosition(note: Pick<OpenWorldNoteTurn, 'attemptId' | 'boundary' | 'playerMessageId' | 'manual'>, chain: NoteBranch,
  awaitingPlayerMessageId?: string | null): number {
  if (note.boundary === 'opening') return 0
  const index = note.manual && note.boundary === 'before'
    ? chain.findIndex(turn => (turn.playerMessageId ?? turn.id) === note.playerMessageId)
    : chain.findIndex(turn => turn.id === note.attemptId)
  if (index >= 0) return index * 2 + (note.boundary === 'before' ? 1 : 2)
  if (note.boundary === 'before' && awaitingPlayerMessageId && note.playerMessageId === awaitingPlayerMessageId) return chain.length * 2 + 1
  return -1
}

export function visibleOpenWorldNotes(turns: readonly OpenWorldNoteTurn[], chain: NoteBranch,
  awaitingPlayerMessageId?: string | null): OpenWorldNoteTurn[] {
  return turns.filter(note => note.status === 'completed' && note.note !== null && openWorldNotePosition(note, chain, awaitingPlayerMessageId) >= 0)
    .sort((left, right) => openWorldNotePosition(left, chain, awaitingPlayerMessageId) - openWorldNotePosition(right, chain, awaitingPlayerMessageId)
      || (left.sequence ?? 0) - (right.sequence ?? 0))
}

export function latestOpenWorldNote(turns: readonly OpenWorldNoteTurn[], chain: NoteBranch,
  awaitingPlayerMessageId?: string | null): OpenWorldActiveNote | null {
  return activeOpenWorldNotes(turns, chain, awaitingPlayerMessageId).at(-1) ?? null
}

export function activeOpenWorldNotes(turns: readonly OpenWorldNoteTurn[], chain: NoteBranch,
  awaitingPlayerMessageId?: string | null): OpenWorldActiveNote[] {
  return visibleOpenWorldNotes(turns, chain, awaitingPlayerMessageId).map(turn => {
    const { noteId, sequence, manual, boundary, playerMessageId, attemptId } = turn
    return { attemptId, note: turn.note!, ...(noteId ? {noteId} : {}), ...(sequence === undefined ? {} : {sequence}),
      ...(manual ? {manual} : {}), ...(boundary ? {boundary} : {}), ...(playerMessageId ? {playerMessageId} : {}) }
  })
}

/** Refresh captured identities only; later checkpoints must not enter a retry. */
export function effectiveOpenWorldNote(note: OpenWorldActiveNote, turns: readonly OpenWorldNoteTurn[]): OpenWorldActiveNote {
  const current = turns.find(turn => openWorldNoteId(turn) === openWorldNoteId(note) && turn.status === 'completed')
  return current?.note ? { ...note, note: current.note } : note
}
